
import {
  Component,
  OnInit,
  EventEmitter,
  Input,
  Output,
  AfterViewInit,
  SimpleChanges,
  OnChanges,
  CUSTOM_ELEMENTS_SCHEMA,
  ChangeDetectorRef
} from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';

type ErrorState = 'warning' | 'error';

@Component({
  selector: 'lib-single-date-picker',
  templateUrl: './single-date-picker.component.html',
  styleUrls: ['./single-date-picker.component.scss'],
  standalone: true,
  providers: [DatePipe],
  imports: [CommonModule],
  schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class SingleDatePickerComponent implements OnInit, AfterViewInit, OnChanges {
  @Output() dateChange: EventEmitter<string> = new EventEmitter();

  @Input() placeholder = 'Date';
  @Input() disabledInput = false;
  @Input() displayDefaultDate = true;
  @Input() isSubmitted = false;
  @Input() dateValue?: any;
  @Input() required = false;
  @Input() customWidthCss = 'datepicker-date';
  @Input() disableFutureDates = false;
  @Input() disablePastDates = false;
  @Input() limitToOneYear = false; 
  @Input() displayLabel = true;
  @Input() minDate?: Date = null;
  @Input() maxDate?: Date = null;
  @Input() showTillYesterday = false;
  @Input() message = '';
  @Input() showError = false;
  @Input() errorState = '';
  @Input() componentName = '';

  public date: any;
  public dateState: ErrorState = null;

  constructor(private datePipe: DatePipe, private cdr: ChangeDetectorRef) {}

  ngOnInit() {
    if (this.displayDefaultDate && !this.dateValue) {
      this.date = this.datePipe.transform(new Date(), 'yyyy-MM-dd');
    }

    this.updateDateLimits();

    if (this.dateValue) {
      this.date = this.datePipe.transform(this.dateValue, 'yyyy-MM-dd');
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['isSubmitted']?.currentValue === true) {
      if (!this.date) {
        this.checkError();
      }
    }
    if (changes['dateValue'] && changes['dateValue'].currentValue) {
      this.date = this.datePipe.transform(this.dateValue, 'yyyy-MM-dd');
      this.updateDateLimits();
    }
  }

  ngAfterViewInit() {
    if (this.displayDefaultDate && !this.dateValue) {
      this.date = this.datePipe.transform(new Date(), 'yyyy-MM-dd');
    }
    if (this.dateValue) {
      this.date = this.datePipe.transform(this.dateValue, 'yyyy-MM-dd');
      this.onDateChange(this.dateValue);
    }
  }

  updateDateLimits() {
    const today = new Date();

    // ✅ Min date logic
    if (this.disablePastDates) {
      this.minDate = today;
    } else {
      this.minDate = this.minDate || null;
    }

    // ✅ Max date logic
    if (this.disableFutureDates) {
      this.maxDate = today;
    } else if (this.limitToOneYear) {
      const max = new Date(today);
      max.setDate(max.getDate() + 365);
      this.maxDate = max;
    } else {
      this.maxDate = this.maxDate || new Date(today.getFullYear() + 1, today.getMonth(), today.getDate());
    }

    this.cdr.detectChanges();
  }

  get minDateValue() {
    return this.minDate ? this.minDate.toISOString() : '';
  }

  get maxDateValue() {
    if (!this.maxDate) return '';
    const max = new Date(this.maxDate.valueOf());
    if (this.showTillYesterday) {
      max.setDate(max.getDate() - 1);
    }
    return max.toISOString();
  }

  onDateChange(date: any, isClearDate?: boolean) {
    this.checkError();

    if (date?.detail !== undefined) {
      this.date = date.detail;
      if (!isClearDate) this.checkError();
      this.dateChange.emit(date.detail);
    } else {
      this.date = typeof date === 'string' ? date : this.datePipe.transform(date, 'yyyy-MM-dd');
      if (!isClearDate) this.checkError();
      this.dateChange.emit(this.date);
    }
  }

  clearDate() {
    this.date = '';
    this.checkError();
  }

  checkError() {
    if (this.required && !this.date) {
      this.showError = true;
      this.dateState = 'error';
      this.message = this.message || `${this.placeholder} is required.`;
    } else {
      this.showError = false;
      this.dateState = null;
      this.message = '';
    }
  }

  onBlur() {
    if (!this.date && this.required) {
      this.showError = true;
      this.dateState = this.isSubmitted ? 'error' : 'warning';
      this.message = `${this.placeholder} is required.`;
    } else {
      this.showError = false;
      this.dateState = null;
      this.message = '';
    }
  }
}
